/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/components/A3ProcessRawMaterialsMultiSelect.tsx.
 */

import React, { useEffect, useId, useRef, useState } from "react";
import {
  parseA3ProcessRawMaterialIds,
  serializeA3ProcessRawMaterialIds,
  serializeA3ProcessRawMaterialNames,
} from "../utils/a3ProcessRawMaterials";

type Option = { id: number; name: string };

type Props = {
  name: string;
  value: string;
  labelValue: string;
  options: Option[];
  placeholder?: string;
  onChange: (patch: Record<string, string>) => void;
};

const A3ProcessRawMaterialsMultiSelect: React.FC<Props> = ({
  name,
  value,
  labelValue,
  options,
  placeholder = "Select raw materials",
  onChange,
}) => {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const selectedIds = new Set(parseA3ProcessRawMaterialIds(value));
  const selectedOptions = options.filter((o) => selectedIds.has(String(o.id)));
  const displayText =
    labelValue.trim() ||
    serializeA3ProcessRawMaterialNames(selectedOptions.map((o) => o.name));

  useEffect(() => {
    if (!open) return;
    const onDocClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const labelKey = `${name}_label`;

  const applySelection = (nextIds: Set<string>) => {
    const ordered = options
      .filter((o) => nextIds.has(String(o.id)))
      .map((o) => String(o.id));
    const labels = options
      .filter((o) => nextIds.has(String(o.id)))
      .map((o) => o.name);

    onChange({
      [name]: serializeA3ProcessRawMaterialIds(ordered),
      [labelKey]: serializeA3ProcessRawMaterialNames(labels),
    });
  };

  const toggleOption = (id: number) => {
    const idStr = String(id);
    const next = new Set(selectedIds);
    if (next.has(idStr)) next.delete(idStr);
    else next.add(idStr);
    applySelection(next);
  };

  return (
    <div
      ref={rootRef}
      className={`a3-rm-multi-select${open ? " is-open" : ""}`}
    >
      <button
        type="button"
        className="a3-rm-multi-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span
          className={
            displayText
              ? "a3-rm-multi-select__value"
              : "a3-rm-multi-select__placeholder"
          }
        >
          {displayText || placeholder}
        </span>
        <span className="a3-rm-multi-select__chevron" aria-hidden />
      </button>

      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          className="a3-rm-multi-select__menu"
        >
          {options.map((o) => {
            const selected = selectedIds.has(String(o.id));
            const optionId = `${name}_option_${o.id}`;
            return (
              <li key={o.id} role="option" aria-selected={selected}>
                <label htmlFor={optionId} className="a3-rm-multi-select__option">
                  <input
                    id={optionId}
                    type="checkbox"
                    className="a3-rm-multi-select__checkbox"
                    checked={selected}
                    onChange={() => toggleOption(o.id)}
                  />
                  <span className="a3-rm-multi-select__option-label">{o.name}</span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

/** React component — A3Process Raw Materials Multi Select. */
export default A3ProcessRawMaterialsMultiSelect;
